import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Plus,
  TrendingUp,
  Clock,
  MessageCircle,
  ThumbsUp,
  AlertCircle,
  Loader2,
  Search,
  Users,
  MessageSquare
} from 'lucide-react';
import { getThreads, toggleThreadLike, getOrCreateForumProfile } from '../Services/ForumService';
import { useAuth } from '../contexts/AuthContext';
import { formatTimeAgo } from '../Services/utils/dateHelpers';
import CreateThreadModal from '../components/forum/CreateThreadModal';

const ForumPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [forumProfile, setForumProfile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (currentUser) loadForumProfile();
  }, [currentUser]);

  useEffect(() => {
    loadThreads();
  }, [sortBy]);

  const loadForumProfile = async () => {
    try {
      const profile = await getOrCreateForumProfile(currentUser.uid);
      setForumProfile(profile);
    } catch (error) {
      console.error('Erreur profil:', error);
    }
  };

  const loadThreads = async () => {
    try {
      setLoading(true);
      const data = await getThreads(sortBy);
      setThreads(data);
    } catch (error) {
      console.error('Erreur threads:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50/50 dark:bg-[#0a0a0b]">
        {/* --- HERO SECTION --- */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
          <div className="max-w-7xl mx-auto px-4 py-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white mb-2">
                  Communauté
                </h1>
                <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl">
                  Partagez vos expériences et développez vos connaissances médicales.
                </p>
              </div>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="h-12 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-all hover:scale-105 active:scale-95 shadow-lg shadow-blue-500/20"
                disabled={!currentUser}
              >
                <Plus className="w-5 h-5 mr-2" />
                Lancer une discussion
              </Button>
            </div>

            {/* Barre de recherche */}
            <div className="relative mt-2">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Recherchez si le sujet existe déjà..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* --- COLONNE GAUCHE: FILTRES & THREADS --- */}
            <div className="lg:col-span-8 space-y-6">
              
              {/* Bar de navigation / Tri */}
              <div className="flex items-center justify-between p-1 bg-gray-100 dark:bg-gray-800/50 rounded-xl w-fit">
                {[
                  { id: 'recent', label: 'Récents', icon: Clock },
                  { id: 'popular', label: 'Populaires', icon: TrendingUp },
                  { id: 'active', label: 'Actifs', icon: MessageSquare },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSortBy(tab.id)}
                    className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      sortBy === tab.id
                        ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 mr-2" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Liste des threads */}
              {loading ? (
                <div className="flex flex-col justify-center items-center py-20 space-y-4">
                  <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
                  <p className="text-gray-500 animate-pulse">Chargement des discussions...</p>
                </div>
              ) : threads.filter(t =>
                  t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  t.content?.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 && searchQuery ? (
                <Card className="p-16 text-center border-dashed border-2">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Aucun sujet trouvé</h3>
                  <p className="text-gray-500 mb-6">
                    Aucune discussion ne correspond à "<span className="font-medium text-gray-700 dark:text-gray-300">{searchQuery}</span>".
                  </p>
                  <Button
                    onClick={() => { setSearchQuery(''); setShowCreateModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white border-0"
                  >
                    Créer ce sujet
                  </Button>
                </Card>
              ) : threads.length === 0 ? (
                <Card className="p-16 text-center border-dashed border-2">
  <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
  <h3 className="text-xl font-semibold mb-2">Le silence est d'or...</h3>
  <p className="text-gray-500 mb-6">Soyez le premier à briser la glace !</p>
  <Button 
    onClick={() => setShowCreateModal(true)} 
    className="bg-blue-600 hover:bg-blue-700 text-white border-0"
  >
    Créer le premier sujet
  </Button>
</Card>
              ) : (
                <div className="grid gap-4">
                  {threads.filter(t =>
                    t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    t.content?.toLowerCase().includes(searchQuery.toLowerCase())
                  ).map((thread) => (
                    <Card
                      key={thread.id}
                      onClick={() => navigate(`/forum/thread/${thread.id}`)}
                      className="group p-5 bg-white dark:bg-[#121214] border-gray-200 dark:border-gray-800 hover:border-blue-400 dark:hover:border-blue-500/50 transition-all cursor-pointer shadow-sm hover:shadow-md"
                    >
                      <div className="flex gap-4">
                        {/* Avatar avec effet d'anneau */}
                        <div
                          className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold ring-4 ring-gray-50 dark:ring-gray-800/50"
                          style={{ backgroundColor: thread.authorAvatar || '#3b82f6' }}
                        >
                          {thread.authorPseudo?.charAt(0).toUpperCase()}
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                              {thread.authorPseudo}
                            </span>
                            <span className="text-gray-400 text-xs">•</span>
                            <span className="text-xs text-gray-500 italic">
                              {formatTimeAgo(thread.createdAt)}
                            </span>
                          </div>

                          <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-2">
                            {thread.title}
                          </h3>
                          
                          <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-4 leading-relaxed">
                            {thread.content}
                          </p>

                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-1.5 text-gray-500 text-sm group/btn">
                              <div className="p-2 rounded-full group-hover/btn:bg-pink-50 dark:group-hover/btn:bg-pink-500/10 group-hover/btn:text-pink-500 transition-colors">
                                <ThumbsUp className="w-4 h-4" />
                              </div>
                              <span className="font-medium">{thread.likes || 0}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-gray-500 text-sm group/btn">
                              <div className="p-2 rounded-full group-hover/btn:bg-blue-50 dark:group-hover/btn:bg-blue-500/10 group-hover/btn:text-blue-500 transition-colors">
                                <MessageCircle className="w-4 h-4" />
                              </div>
                              <span className="font-medium">{thread.repliesCount || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* --- COLONNE DROITE: INFOS & RÈGLES --- */}
            <div className="lg:col-span-4 space-y-6">
              {/* Carte Profil Rapide */}
              {currentUser && (
                <Card className="p-6 bg-gradient-to-br from-blue-600 to-indigo-700 border-none text-white overflow-hidden relative">
                  <div className="relative z-10">
                    <h4 className="font-bold text-lg mb-1">Mon activité</h4>
                    <p className="text-blue-100 text-sm mb-4">Content de vous revoir !</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                        <div className="text-2xl font-bold">{forumProfile?.threadsCount || 0}</div>
                        <div className="text-xs text-blue-200 uppercase tracking-wider">Sujets</div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                        <div className="text-2xl font-bold">{forumProfile?.reputation || 0}</div>
                        <div className="text-xs text-blue-200 uppercase tracking-wider">Points</div>
                      </div>
                    </div>
                  </div>
                  <Users className="absolute -bottom-4 -right-4 w-24 h-24 text-white/10 rotate-12" />
                </Card>
              )}

              {/* Règles du forum retravaillées */}
              <Card className="p-6 border-amber-100 dark:border-amber-900/30 bg-amber-50/50 dark:bg-amber-900/10">
                <div className="flex items-center gap-2 mb-4 text-amber-700 dark:text-amber-400">
                  <AlertCircle className="w-5 h-5" />
                  <h4 className="font-bold">Charte d'utilisation</h4>
                </div>
                <ul className="space-y-3 text-sm text-amber-900/80 dark:text-amber-200/70">
                  <li className="flex gap-2">
                    <span className="text-amber-500 font-bold">•</span>
                    Pas de conseils médicaux remplaçant un docteur.
                  </li>
                  <li className="flex gap-2">
                    <span className="text-amber-500 font-bold">•</span>
                    Bienveillance et respect mutuel exigés.
                  </li>
                  <li className="flex gap-2">
                    <span className="text-amber-500 font-bold">•</span>
                    Recherchez si le sujet existe déjà.
                  </li>
                </ul>
              </Card>

              <div className="text-center px-6">
                <p className="text-xs text-gray-400">
                  Besoin d'aide ? <a href="#" className="underline hover:text-blue-500">Contactez un modérateur</a>
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateThreadModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadThreads();
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default ForumPage;